import React, { useState, useEffect } from 'react';
import DataTable from '../../components/ui/DataTable';
import EmailQueryDetailsModal from '../../components/modals/EmailQueryDetailsModal';
import { listEmailQueries, updateEmailQuery, deleteEmailQuery, getEmailQueryStats, getEmailQuery } from '../../services/cms';

const EmailQueriesTab = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ new: 0, read: 0, replied: 0, archived: 0, total: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    search: ''
  });
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingQuery, setLoadingQuery] = useState(false);

  useEffect(() => {
    loadQueries();
    loadStats();
  }, [pagination.page, filters]);

  const loadQueries = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.source && { source: filters.source }),
        ...(filters.search && { search: filters.search })
      };

      const response = await listEmailQueries(params);
      if (response && response.success !== false && response.data) {
        setQueries(response.data);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            pages: response.pagination.pages
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load email queries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getEmailQueryStats();
      if (response && response.success !== false && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      setLoadingQuery(true);
      const response = await getEmailQuery(id);
      if (response && response.success !== false && response.data) {
        setSelectedQuery(response.data);
        setIsModalOpen(true);
      } else {
        // Show error toast or message
        console.error('Failed to load email query details');
      }
    } catch (error) {
      console.error('Failed to load email query details:', error);
    } finally {
      setLoadingQuery(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await updateEmailQuery(id, { status: newStatus });
      if (response && response.success !== false) {
        loadQueries();
        loadStats();
        // Update selected query if modal is open
        if (selectedQuery && selectedQuery._id === id) {
          setSelectedQuery({ ...selectedQuery, status: newStatus });
        }
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email query?')) return;

    try {
      const response = await deleteEmailQuery(id);
      if (response && response.success !== false) {
        loadQueries();
        loadStats();
        // Close modal if deleted query is currently open
        if (selectedQuery && selectedQuery._id === id) {
          setIsModalOpen(false);
          setSelectedQuery(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete email query:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'new': 'bg-blue-100 text-blue-700',
      'read': 'bg-yellow-100 text-yellow-700',
      'replied': 'bg-green-100 text-green-700',
      'archived': 'bg-gray-100 text-gray-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const columns = [
    {
      header: 'Name',
      accessor: 'name',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.email}</div>
        </div>
      )
    },
    {
      header: 'Subject',
      accessor: 'subject',
      render: (value) => value || <span className="text-gray-400">No subject</span>
    },
    {
      header: 'Message',
      accessor: 'message',
      render: (value) => (
        <div className="max-w-md truncate text-sm text-gray-600">
          {value}
        </div>
      )
    },
    {
      header: 'Source',
      accessor: 'source',
      render: (value) => (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          {value || 'contact-form'}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value, row) => (
        <select
          value={value}
          onChange={(e) => handleStatusChange(row._id, e.target.value)}
          className={`px-2 py-1 rounded-full text-xs font-medium border-0 ${getStatusBadge(value)}`}
        >
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
          <option value="archived">Archived</option>
        </select>
      )
    },
    {
      header: 'Date',
      accessor: 'createdAt',
      render: (value) => new Date(value).toLocaleDateString()
    }
  ];

  const actionMenuItems = (row) => [
    {
      label: 'View Details',
      onClick: () => handleViewDetails(row._id),
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10.3866 8.00001C10.3866 9.31334 9.31995 10.38 7.99995 10.38C6.67995 10.38 5.61328 9.31334 5.61328 8.00001C5.61328 6.68668 6.67995 5.62001 7.99995 5.62001C9.31995 5.62001 10.3866 6.68668 10.3866 8.00001Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8.00004 13.5133C10.3534 13.5133 12.5467 12.1267 14.0734 9.72668C14.6734 8.78668 14.6734 7.20668 14.0734 6.26668C12.5467 3.86668 10.3534 2.48001 8.00004 2.48001C5.64671 2.48001 3.45337 3.86668 1.92671 6.26668C1.32671 7.20668 1.32671 8.78668 1.92671 9.72668C3.45337 12.1267 5.64671 13.5133 8.00004 13.5133Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      label: 'Delete',
      onClick: () => handleDelete(row._id),
      danger: true,
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2762 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33334 13.687 3.33334 13.3333V4M5.33334 4V2.66667C5.33334 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33334C9.68696 1.33333 10.0261 1.47381 10.2762 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    }
  ];

  if (loading && queries.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500 mb-1">Total</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="text-sm text-blue-600 mb-1">New</div>
          <div className="text-2xl font-bold text-blue-700">{stats.new}</div>
        </div>
        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="text-sm text-yellow-600 mb-1">Read</div>
          <div className="text-2xl font-bold text-yellow-700">{stats.read}</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-sm text-green-600 mb-1">Replied</div>
          <div className="text-2xl font-bold text-green-700">{stats.replied}</div>
        </div>
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Archived</div>
          <div className="text-2xl font-bold text-gray-700">{stats.archived}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters({ ...filters, status: e.target.value });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
          <select
            value={filters.source}
            onChange={(e) => {
              setFilters({ ...filters, source: e.target.value });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            <option value="">All Sources</option>
            <option value="contact-form">Contact Form</option>
            <option value="newsletter">Newsletter</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
          <input
            type="text"
            placeholder="Search by name, email, subject, or message..."
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={queries}
        loading={loading}
        actionMenuItems={actionMenuItems}
        pagination={pagination}
        currentPage={pagination.page}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />

      {/* Email Query Details Modal */}
      <EmailQueryDetailsModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedQuery(null);
        }}
        query={selectedQuery}
      />
    </div>
  );
};

export default EmailQueriesTab;

